/**
 * IndexedDB persistence for the image editor.
 *
 * Schema (DB_VERSION 5):
 *  - `sessions` — one record per open tab, keyed by `documentId` (a UUID
 *    minted at tab creation, also mirrored as `PersistedSession.documentId`).
 *    Each record is a serialized {@link PersistedSession}. Records survive
 *    independently — closing one tab doesn't affect the others, and a
 *    browser reload restores every tab that was persisted.
 *  - `meta` — single record at key `'config'` holding the `lastActiveDocumentId`
 *    hint so reload restores the same tab as active rather than picking
 *    arbitrarily.
 *  - `psd-stashes` — keyed by `documentId`. Populated by `psd-source-stash.ts`
 *    for lossless PSD round-trip (Tier 2 Group F sub-piece #2).
 *  - `svg-stashes` — keyed by `documentId`. Populated by `svg-source-stash.ts`
 *    for lossless SVG round-trip when sub-piece #4 SVG export lands
 *    (Tier 2 Group F sub-piece #3).
 *  - `user-export-presets` — keyed by `id` (UUID). User-saved export
 *    presets surfaced in the ExportDialog dropdown. Tab-lifecycle-
 *    independent (NOT wiped by `clearSession` / `clearAllSessions`);
 *    they're user-scoped, not per-tab. Tier 2 Group F sub-piece #8.
 *
 * Migration path:
 *  - v1 (pre-Tier 1): stored layers as a flat `Layer[]` under `layers`.
 *  - v2 (Tier 1): stores the `EditorDocument` tree under `document`. Single
 *    record at key `'current'`. v1→v2 promotion runs in-app via
 *    `migrateSession` on load.
 *  - v3 (Tier 2 Group F): multi-record sessions keyed by `documentId`. The
 *    upgrade transaction renames the pre-existing `'current'` record to a
 *    fresh UUID + populates the matching meta record so reload restores it.
 *  - v4 (Tier 2 Group F sub-piece #3): adds `svg-stashes` object store.
 *    Pure additive — no data migration; the new store is created empty.
 *    `clearSession` + `clearAllSessions` now also wipe the new store so
 *    a tab close removes its SVG stash atomically.
 *  - v5 (Tier 2 Group F sub-piece #8): adds `user-export-presets` object
 *    store. Pure additive — no data migration; the new store is created
 *    empty. NOT wiped by `clearSession` / `clearAllSessions` (presets
 *    are user-scoped, not per-tab).
 *  - v6: NO new store. A no-op version bump that re-fires
 *    `onupgradeneeded` so the idempotent `contains()` guards re-create
 *    any store that went missing on a client whose DB version raced
 *    ahead of the store-creation code (dev HMR drift). Self-heals; no
 *    data migration.
 *
 * **Migration trip-wire — DO NOT REPEAT THE v2→v3 STRANDING BUG.**
 *
 *   - **Pure-additive bumps** (v3→v4 svg-stashes, v4→v5
 *     user-export-presets) introduce a new empty store. The
 *     `if (!db.objectStoreNames.contains(NAME))` guard inside
 *     `onupgradeneeded` is sufficient AND idempotent across all
 *     paths (fresh install, upgrade-from-direct-predecessor,
 *     upgrade-from-multiple-back). NO heal function needed.
 *
 *   - **Restructuring bumps** (v2→v3 re-keyed the single 'current'
 *     record to a UUID) MUST add a matching `healOrphanedXxx`
 *     function called from every load path, the same way
 *     `healOrphanedLegacyRecord` patches up the v2→v3 transition
 *     for users whose upgrade transaction partial-failed.
 *
 * Adding a no-op heal function to a pure-additive bump is a
 * performance regression (extra read tx on every load) AND
 * misleading code — don't.
 *
 * NOTE: File System Access API handles (`FileSystemFileHandle`, kept on
 * `TabDoc.fileHandle` to enable in-place save) are intentionally NOT
 * persisted here. While handles are technically structuredClone-able and
 * IDB-storable in modern Chromium, restoring them on the next session
 * requires a fresh `queryPermission()` re-grant prompt — which feels
 * worse than the current behavior (Save degrades to a download in this
 * one edge case). If this becomes a frequent papercut, a follow-up PR
 * can add an `idb-keyval`-style handle store + a one-click re-grant UI.
 */

import type { Layer, EditorDocument, ViewSettings, SliceRect } from '../types';
import { fromFlatLayers } from './layer-tree';
import type { HistoryEntryWithState } from './canvas-history';

const DB_NAME = 'fias-image-editor';
const SESSIONS_STORE = 'sessions';
const META_STORE = 'meta';
const PSD_STASHES_STORE = 'psd-stashes';
const SVG_STASHES_STORE = 'svg-stashes';
const USER_EXPORT_PRESETS_STORE = 'user-export-presets';
// v6: no NEW store — a no-op version bump whose sole purpose is to
// re-fire `onupgradeneeded` on clients whose DB reached v5 before the
// `user-export-presets` createObjectStore line was in their running
// bundle (dev HMR / partial-build drift, where a store can be missing
// even though the version says 5). The existing idempotent `contains()`
// guards create any missing store and no-op for healthy DBs, so this
// self-heals the drift with zero data migration. Harmless in prod (no
// real user can be in that state) but cheap insurance.
const DB_VERSION = 6;
const META_KEY = 'config';
/** Legacy v1/v2 single-record key. Used only by the v3 upgrade to find
 *  the existing record and re-key it to a UUID. New writes never use it. */
const LEGACY_SESSION_KEY = 'current';

export interface PersistedSession {
  /** Stable UUID minted at tab creation; doubles as the IDB key for both
   *  this session record and any associated PSD-stash entry. Survives
   *  reload (it's persisted alongside the rest of the snapshot). */
  documentId: string;
  canvasJSON: object;
  fileName: string;
  fileId: string | null;
  /** Legacy: Fabric brightness/contrast/saturation filters from
   *  before the Adjustments dialog landed. New sessions don't
   *  write this (no UI to set the values); old sessions on read
   *  silently ignore it. Optional purely for back-compat with
   *  records persisted under the previous schema. */
  filters?: { brightness: number; contrast: number; saturation: number };
  /** Fabric viewport transform [a, b, c, d, e, f] — captures zoom + pan. */
  viewportTransform?: number[];
  /** v2: the full layer document. Authoritative shape. Older
   *  records persisted `layers` instead — `migrateSession` promotes
   *  those records by promoting `layers` → a flat root-only
   *  document. New sessions only write `document`. */
  document?: EditorDocument;
  /** v1 (legacy): flat ordered layer list. Promoted to `document`
   *  on read. Newly-written sessions must NOT include this; the
   *  type stays optional only for the transitional read path. */
  layers?: Layer[];
  activeLayerId?: string | null;
  /** Undo/redo stack — each entry is the same shape as a `useCanvasHistory` snapshot. */
  historyStates?: { canvasJSON: object; extra?: unknown }[];
  /** Position within historyStates of the "current" state. */
  historyIndex?: number;
  /** Rich undo/redo stack — preserves per-entry metadata (label,
   *  commandId, isManual, timestamp, id) across reload. Newer sessions
   *  write this INSTEAD of `historyStates` so the History panel reads
   *  "Brush", "Rotate left 90°", etc. after refresh instead of falling
   *  back to synthesized "Step N" placeholders. The reader prefers
   *  `historyEntries` when present and falls back to `historyStates`
   *  for pre-Tier 3 records. */
  historyEntries?: HistoryEntryWithState[];
  /** Tier 2 Group D — workspace overlay state (rulers / grid /
   *  guides toggles + grid config + guide list + snap toggles).
   *  Optional for back-compat: sessions persisted before Group D
   *  load with `viewSettings: undefined` and the reducer keeps
   *  its defaults. */
  viewSettings?: ViewSettings;
  /** Tier 2 Group F sub-piece #5 — named rectangular slice
   *  regions. Optional for back-compat: sessions persisted before
   *  the Slice tool load with `slices: undefined` and the reducer
   *  keeps its empty-array default. No IDB schema bump required;
   *  this is a pure additive field on the existing record shape. */
  slices?: SliceRect[];
  /** Tier 3 Group D Slice 1d — document DPI metadata. Optional
   *  for back-compat: sessions persisted before slice 1d load with
   *  `dpi: undefined` and the reducer keeps its `null` default
   *  (which downstream code treats as "DPI unspecified"). */
  dpi?: number | null;
  /** Original filename-with-extension captured at open time. Used
   *  by the Export dialog's format-dropdown default so opening a
   *  JPEG and clicking Save-As lands on JPEG (not PNG). Optional
   *  for back-compat: sessions persisted before this field load
   *  with `sourceFileName: undefined` and the dialog falls back to
   *  `fileName` (which is extension-stripped, so format defaults
   *  to PNG — same behavior as the pre-field code path). */
  sourceFileName?: string | null;
  /** `state.isDirty` at save time. Restored on load so the tab's `*`
   *  unsaved-indicator survives a refresh — without this, the dirty
   *  flag resets to false on reload even though the in-memory and
   *  on-disk states differ from the last explicit save. Optional for
   *  back-compat: pre-field records load as `undefined` and the
   *  reducer keeps its `false` default. */
  isDirty?: boolean;
  savedAt: number;
}

interface StoredSessionRecord extends PersistedSession {
  /** Mirrors `documentId` — keyPath on the IDB object store. Set on
   *  write, stripped on read so callers see a clean `PersistedSession`. */
  id: string;
}

interface MetaRecord {
  /** Sentinel key — there's exactly one meta record. */
  key: typeof META_KEY;
  lastActiveDocumentId: string | null;
}

/** Object-store names exposed for cross-module IDB transactions
 *  (see `psd-source-stash.ts`). Treat these as constants; mutating
 *  them silently breaks any shared transaction. */
export const SESSION_STORE_NAMES = Object.freeze({
  sessions: SESSIONS_STORE,
  meta: META_STORE,
  psdStashes: PSD_STASHES_STORE,
  svgStashes: SVG_STASHES_STORE,
  userExportPresets: USER_EXPORT_PRESETS_STORE,
} as const);

/** Open the editor's IndexedDB connection at the current schema
 *  version, running the v1→v2/v2→v3 upgrade transaction inline.
 *  Exported so sibling modules (PSD stash) can share the same DB
 *  without duplicating the upgrade logic. */
export function openImageEditorDb(): Promise<IDBDatabase> {
  return openDb();
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = request.result;
      const oldVersion = event.oldVersion;
      // Use the implicit upgrade transaction to run the in-DB migration.
      const tx = request.transaction;
      if (!tx) return;

      // Sessions store has existed since v1; create if first-time install.
      if (!db.objectStoreNames.contains(SESSIONS_STORE)) {
        db.createObjectStore(SESSIONS_STORE, { keyPath: 'id' });
      }
      // v3: new meta store for the lastActiveDocumentId hint.
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'key' });
      }
      // v3: new psd-stashes store. Empty here; populated in a follow-up
      // phase when PSD import lands. Creating it now means the schema
      // upgrade happens once, not twice.
      if (!db.objectStoreNames.contains(PSD_STASHES_STORE)) {
        db.createObjectStore(PSD_STASHES_STORE, { keyPath: 'documentId' });
      }
      // v4: new svg-stashes store for the SVG round-trip path. Purely
      // additive — no data migration. Created idempotently via the
      // `contains` guard so a v3→v4 upgrade (existing user) and a
      // fresh v4 install (new user) hit the same code path.
      if (!db.objectStoreNames.contains(SVG_STASHES_STORE)) {
        db.createObjectStore(SVG_STASHES_STORE, { keyPath: 'documentId' });
      }
      // v5: new user-export-presets store. Same additive pattern as
      // v4 — `contains` guard makes the createObjectStore idempotent
      // across fresh install / upgrade-from-v4 / upgrade-from-earlier
      // paths. NO heal-on-load function needed (no data to migrate)
      // — see the file-level migration trip-wire comment.
      if (!db.objectStoreNames.contains(USER_EXPORT_PRESETS_STORE)) {
        db.createObjectStore(USER_EXPORT_PRESETS_STORE, { keyPath: 'id' });
      }

      // v2 → v3 migration: the existing single record at key 'current'
      // gets re-keyed to a fresh UUID and gains a `documentId` field;
      // meta record points at it so reload restores it as the active
      // tab. We do this inside the upgrade transaction so the rename
      // is atomic — a half-migrated DB would lose the user's session.
      if (oldVersion > 0 && oldVersion < 3) {
        const sessionsStore = tx.objectStore(SESSIONS_STORE);
        const getReq = sessionsStore.get(LEGACY_SESSION_KEY);
        getReq.onsuccess = () => {
          const record = getReq.result as StoredSessionRecord | undefined;
          if (!record) return;
          // If the record is corrupt (missing / non-object canvasJSON),
          // delete it rather than leaving it stranded at the legacy
          // key. Without this, `loadAllSessions` filters it out on read
          // but the record sits in IDB forever wasting space — typically
          // 50KB–100MB depending on what got persisted.
          if (typeof record.canvasJSON !== 'object') {
            sessionsStore.delete(LEGACY_SESSION_KEY);
            return;
          }
          const newId = crypto.randomUUID();
          // Delete first, then add — keyPath is 'id', and we can't put
          // with a new id while the old record still exists at the
          // legacy key.
          sessionsStore.delete(LEGACY_SESSION_KEY);
          const migrated: StoredSessionRecord = {
            ...record,
            id: newId,
            documentId: newId,
          };
          sessionsStore.put(migrated);
          // Set the active-tab hint to the freshly-migrated record so
          // reload picks it up — but only if the user doesn't already
          // have one pointing at a UUID-keyed tab. Without the guard,
          // a coexisting fresh UUID record + a stranded `'current'`
          // record would surprise-activate the stranded one after the
          // upgrade. See the matching guard in `healOrphanedLegacyRecord`
          // for the post-upgrade path.
          const metaStore = tx.objectStore(META_STORE);
          const metaGet = metaStore.get(META_KEY);
          metaGet.onsuccess = () => {
            const meta = metaGet.result as MetaRecord | undefined;
            if (!meta || meta.lastActiveDocumentId == null) {
              metaStore.put({ key: META_KEY, lastActiveDocumentId: newId });
            }
          };
        };
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Defensive cleanup for any stranded legacy `'current'` record left
 *  behind by a botched v2→v3 upgrade transaction. The original
 *  migration runs inside `onupgradeneeded` and only fires when the
 *  oldVersion is < 3 — once a user is past that boundary, a stranded
 *  record at key `'current'` never gets re-migrated, and the multi-tab
 *  load path returns it with `documentId: undefined`, breaking restore
 *  semantics (the next save then clobbers `id: 'current'` again,
 *  perpetuating the cycle).
 *
 *  This function runs on every openDb cycle via a regular (non-upgrade)
 *  transaction. Idempotent — when no `'current'` record exists it
 *  returns immediately. When one is found, it re-migrates the record
 *  to a fresh UUID + sets the meta hint, matching what the original
 *  v2→v3 migration would have done.
 *
 *  Caller is `loadAllSessions` (and friends) — it awaits this before
 *  walking the sessions store so the re-migrated record participates
 *  in the load instead of being filtered out. */
async function healOrphanedLegacyRecord(db: IDBDatabase): Promise<void> {
  return new Promise((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction([SESSIONS_STORE, META_STORE], 'readwrite');
    } catch (err) {
      // db closed / version-changed mid-flight — best-effort, just bail.
      console.warn('[image-editor] healOrphanedLegacyRecord open-tx failed:', err);
      resolve();
      return;
    }
    const sessionsStore = tx.objectStore(SESSIONS_STORE);
    const getReq = sessionsStore.get(LEGACY_SESSION_KEY);
    getReq.onsuccess = () => {
      const record = getReq.result as StoredSessionRecord | undefined;
      if (!record) {
        // Common case — no stranded record. Let the transaction
        // complete as a no-op.
        return;
      }
      if (typeof record.canvasJSON !== 'object') {
        // Corrupt legacy record — just delete it.
        sessionsStore.delete(LEGACY_SESSION_KEY);
        return;
      }
      const newId = crypto.randomUUID();
      sessionsStore.delete(LEGACY_SESSION_KEY);
      const migrated: StoredSessionRecord = {
        ...record,
        id: newId,
        documentId: newId,
      };
      sessionsStore.put(migrated);
      // Wire up the active-tab hint so reload activates the re-keyed
      // record. Only set it if there's no hint already (we don't want
      // to clobber a user's lastActiveDocumentId from a UUID-keyed tab
      // that happens to coexist).
      const metaStore = tx.objectStore(META_STORE);
      const metaGet = metaStore.get(META_KEY);
      metaGet.onsuccess = () => {
        const meta = metaGet.result as MetaRecord | undefined;
        if (!meta || meta.lastActiveDocumentId == null) {
          metaStore.put({ key: META_KEY, lastActiveDocumentId: newId });
        }
      };
      console.info(
        '[image-editor] healOrphanedLegacyRecord re-migrated stranded "current" record to',
        newId,
      );
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => {
      console.warn('[image-editor] healOrphanedLegacyRecord tx failed:', tx.error);
      resolve();
    };
  });
}

/** Load every persisted session. Ordering is by `savedAt` ascending so
 *  the caller can iterate in approximate creation order. */
export async function loadAllSessions(): Promise<PersistedSession[]> {
  try {
    const db = await openDb();
    // Heal any stranded legacy `'current'` record BEFORE the read so
    // the re-migrated record participates in this load cycle. Without
    // this, users whose v2→v3 upgrade callback didn't fire during
    // their initial schema bump end up with a record that the load
    // path returns with `documentId: undefined` (or one that triggers
    // the `id: 'current'` clobber cycle on every save). The heal is
    // idempotent — for users without a stranded record, it commits a
    // no-op transaction.
    await healOrphanedLegacyRecord(db);
    return new Promise((resolve) => {
      const tx = db.transaction(SESSIONS_STORE, 'readonly');
      const store = tx.objectStore(SESSIONS_STORE);
      const request = store.getAll();
      request.onsuccess = () => {
        const records = (request.result as StoredSessionRecord[]) ?? [];
        const sessions = records
          .filter((r) => typeof r.canvasJSON === 'object')
          .map((r) => {
            const { id: _id, ...session } = r;
            return migrateSession(session);
          })
          .sort((a, b) => a.savedAt - b.savedAt);
        resolve(sessions);
      };
      request.onerror = () => {
        console.warn('[image-editor] loadAllSessions failed:', request.error);
        resolve([]);
      };
    });
  } catch (err) {
    console.warn('[image-editor] loadAllSessions threw:', err);
    return [];
  }
}

/** Returns the documentId of the tab that was active when the page was
 *  last unloaded, or null if there's no hint (first install / fresh DB). */
export async function loadLastActiveDocumentId(): Promise<string | null> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(META_STORE, 'readonly');
      const store = tx.objectStore(META_STORE);
      const request = store.get(META_KEY);
      request.onsuccess = () => {
        const record = request.result as MetaRecord | undefined;
        resolve(record?.lastActiveDocumentId ?? null);
      };
      request.onerror = () => {
        console.warn('[image-editor] loadLastActiveDocumentId failed:', request.error);
        resolve(null);
      };
    });
  } catch (err) {
    console.warn('[image-editor] loadLastActiveDocumentId threw:', err);
    return null;
  }
}

/** Promote a v1 (flat-array) session to v2 (document-tree).
 *  Already-v2 sessions pass through unchanged. The check is the
 *  presence of `document` rather than a version field — IndexedDB's
 *  schema version covers the object-store layout, but the record
 *  shape change is layer-data-only and benefits from the simpler
 *  in-app check. New writes always populate `document` only. */
function migrateSession(session: PersistedSession): PersistedSession {
  if (session.document) {
    // v2+ record. Strip any vestigial `layers` field a half-written
    // intermediate session might carry.
    if (session.layers) {
      const { layers: _legacy, ...rest } = session;
      return rest;
    }
    return session;
  }
  if (session.layers && session.layers.length > 0) {
    // v1 record. Promote to a flat document, drop the legacy field.
    const { layers, ...rest } = session;
    return { ...rest, document: fromFlatLayers(layers) };
  }
  // Empty / unknown record — no layers to migrate.
  const { layers: _maybeEmpty, ...rest } = session;
  return rest;
}

export async function saveSession(session: PersistedSession): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(SESSIONS_STORE, 'readwrite');
      const store = tx.objectStore(SESSIONS_STORE);
      const record: StoredSessionRecord = { ...session, id: session.documentId };
      const request = store.put(record);
      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.warn('[image-editor] saveSession failed:', request.error);
        resolve();
      };
    });
  } catch (err) {
    console.warn('[image-editor] saveSession threw:', err);
  }
}

export async function saveLastActiveDocumentId(documentId: string | null): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(META_STORE, 'readwrite');
      const store = tx.objectStore(META_STORE);
      const request = store.put({ key: META_KEY, lastActiveDocumentId: documentId });
      request.onsuccess = () => resolve();
      request.onerror = () => {
        console.warn('[image-editor] saveLastActiveDocumentId failed:', request.error);
        resolve();
      };
    });
  } catch (err) {
    console.warn('[image-editor] saveLastActiveDocumentId threw:', err);
  }
}

/** Delete one session record AND its matching stash entries (if any)
 *  in a single transaction. Use this when a tab closes — orphan
 *  stash entries would otherwise occupy IDB until the next startup-time
 *  GC pass. Wipes both `psd-stashes` and `svg-stashes` — they're keyed
 *  identically (by documentId) but a given tab only ever populates one
 *  of them; the unused delete is a no-op. */
export async function clearSession(documentId: string): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(
        [SESSIONS_STORE, PSD_STASHES_STORE, SVG_STASHES_STORE, META_STORE],
        'readwrite',
      );
      tx.objectStore(SESSIONS_STORE).delete(documentId);
      tx.objectStore(PSD_STASHES_STORE).delete(documentId);
      tx.objectStore(SVG_STASHES_STORE).delete(documentId);
      // If this was the last-active tab, scrub the meta hint so reload
      // doesn't try to activate a tab that no longer exists.
      const metaStore = tx.objectStore(META_STORE);
      const getReq = metaStore.get(META_KEY);
      getReq.onsuccess = () => {
        const record = getReq.result as MetaRecord | undefined;
        if (record && record.lastActiveDocumentId === documentId) {
          metaStore.put({ key: META_KEY, lastActiveDocumentId: null });
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        console.warn('[image-editor] clearSession failed:', tx.error);
        resolve();
      };
    });
  } catch (err) {
    console.warn('[image-editor] clearSession threw:', err);
  }
}

/** Wipe all sessions + meta + stashes. Used when the last tab closes — see
 *  the rationale in `EditorPage.tsx` (a stale snapshot in a freshly-empty
 *  editor has been observed to break the marquee + move tools on next open). */
export async function clearAllSessions(): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(
        [SESSIONS_STORE, PSD_STASHES_STORE, SVG_STASHES_STORE, META_STORE],
        'readwrite',
      );
      tx.objectStore(SESSIONS_STORE).clear();
      tx.objectStore(PSD_STASHES_STORE).clear();
      tx.objectStore(SVG_STASHES_STORE).clear();
      tx.objectStore(META_STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        console.warn('[image-editor] clearAllSessions failed:', tx.error);
        resolve();
      };
    });
  } catch (err) {
    console.warn('[image-editor] clearAllSessions threw:', err);
  }
}
